<script setup>
// WEXHUB: alta de una plantilla de WhatsApp. Va en un modal, igual que el alta
// de respuestas predefinidas, para no dejar el formulario suelto en la pagina.
import { ref, computed } from 'vue';
import { useI18n } from 'vue-i18n';
import { useAlert } from 'dashboard/composables';
import WhatsappTemplatesAPI from 'dashboard/api/whatsappTemplates';
import NextButton from 'dashboard/components-next/button/Button.vue';
import Input from 'dashboard/components-next/input/Input.vue';

const props = defineProps({
  inboxId: { type: Number, required: true },
  onClose: { type: Function, default: () => {} },
});

const emit = defineEmits(['created']);

const { t } = useI18n();

const CATEGORIES = ['UTILITY', 'MARKETING', 'AUTHENTICATION'];

const form = ref({
  name: '',
  category: 'UTILITY',
  language: 'es',
  header: '',
  body: '',
  footer: '',
});
const bodyExamples = ref([]);
const isSubmitting = ref(false);
const formError = ref('');

// Meta rechaza la plantilla si el cuerpo usa {{1}}, {{2}}... y no se manda un
// ejemplo por cada variable. Se detectan en vivo para pedirlos antes de enviar.
const placeholders = computed(() => {
  const matches = form.value.body.match(/\{\{(\d+)\}\}/g) || [];
  return [...new Set(matches)].sort();
});

const syncExamples = () => {
  const needed = placeholders.value.length;
  const current = bodyExamples.value;
  bodyExamples.value = Array.from(
    { length: needed },
    (_, i) => current[i] || ''
  );
};

const previewText = computed(() => {
  let text = form.value.body;
  placeholders.value.forEach((placeholder, index) => {
    const example = bodyExamples.value[index];
    if (example) text = text.split(placeholder).join(example);
  });
  return text;
});

const isIncomplete = computed(() => {
  if (!form.value.name || !form.value.body) return true;
  if (!/^[a-z0-9_]+$/.test(form.value.name)) return true;
  return bodyExamples.value.some(example => !example);
});

const submit = async () => {
  isSubmitting.value = true;
  formError.value = '';
  try {
    await WhatsappTemplatesAPI.createTemplate(props.inboxId, {
      ...form.value,
      body_examples: bodyExamples.value,
    });
    useAlert(t('INBOX_MGMT.WHATSAPP_TEMPLATES.CREATED'));
    emit('created');
    props.onClose();
  } catch (error) {
    // El motivo real de Meta es mucho mas util que un mensaje generico.
    formError.value =
      error?.response?.data?.error ||
      t('INBOX_MGMT.WHATSAPP_TEMPLATES.CREATE_ERROR');
  } finally {
    isSubmitting.value = false;
  }
};
</script>

<template>
  <div class="flex overflow-auto flex-col h-auto">
    <woot-modal-header
      :header-title="$t('INBOX_MGMT.WHATSAPP_TEMPLATES.NEW')"
      :header-content="$t('INBOX_MGMT.WHATSAPP_TEMPLATES.FORM.SUBTITLE')"
    />
    <form class="flex flex-col gap-4 w-full" @submit.prevent="submit">
      <div class="grid gap-4 md:grid-cols-3">
        <label class="flex flex-col gap-1">
          <span class="text-sm text-n-slate-12">
            {{ $t('INBOX_MGMT.WHATSAPP_TEMPLATES.FORM.NAME') }}
          </span>
          <Input
            v-model="form.name"
            placeholder="aviso_de_turno"
            :message="$t('INBOX_MGMT.WHATSAPP_TEMPLATES.FORM.NAME_HINT')"
          />
        </label>
        <label class="flex flex-col gap-1">
          <span class="text-sm text-n-slate-12">
            {{ $t('INBOX_MGMT.WHATSAPP_TEMPLATES.FORM.CATEGORY') }}
          </span>
          <select v-model="form.category">
            <option v-for="option in CATEGORIES" :key="option" :value="option">
              {{ option }}
            </option>
          </select>
        </label>
        <label class="flex flex-col gap-1">
          <span class="text-sm text-n-slate-12">
            {{ $t('INBOX_MGMT.WHATSAPP_TEMPLATES.FORM.LANGUAGE') }}
          </span>
          <Input v-model="form.language" placeholder="es" />
        </label>
      </div>

      <label class="flex flex-col gap-1">
        <span class="text-sm text-n-slate-12">
          {{ $t('INBOX_MGMT.WHATSAPP_TEMPLATES.FORM.HEADER') }}
        </span>
        <Input v-model="form.header" />
      </label>

      <label class="flex flex-col gap-1">
        <span class="text-sm text-n-slate-12">
          {{ $t('INBOX_MGMT.WHATSAPP_TEMPLATES.FORM.BODY') }}
        </span>
        <textarea
          v-model="form.body"
          rows="4"
          :placeholder="
            $t('INBOX_MGMT.WHATSAPP_TEMPLATES.FORM.BODY_PLACEHOLDER')
          "
          @input="syncExamples"
        />
      </label>

      <div v-if="placeholders.length" class="flex flex-col gap-2">
        <span class="text-sm text-n-slate-12">
          {{ $t('INBOX_MGMT.WHATSAPP_TEMPLATES.FORM.EXAMPLES') }}
        </span>
        <p class="m-0 text-xs text-n-slate-11">
          {{ $t('INBOX_MGMT.WHATSAPP_TEMPLATES.FORM.EXAMPLES_HINT') }}
        </p>
        <div class="grid gap-2 md:grid-cols-2">
          <label
            v-for="(placeholder, index) in placeholders"
            :key="placeholder"
            class="flex gap-2 items-center"
          >
            <span class="w-12 font-mono text-xs text-n-slate-11">
              {{ placeholder }}
            </span>
            <Input v-model="bodyExamples[index]" class="flex-1" />
          </label>
        </div>
      </div>

      <label class="flex flex-col gap-1">
        <span class="text-sm text-n-slate-12">
          {{ $t('INBOX_MGMT.WHATSAPP_TEMPLATES.FORM.FOOTER') }}
        </span>
        <Input v-model="form.footer" />
      </label>

      <div v-if="form.body" class="flex flex-col gap-2">
        <span class="text-sm text-n-slate-12">
          {{ $t('INBOX_MGMT.WHATSAPP_TEMPLATES.FORM.PREVIEW') }}
        </span>
        <!-- Se imita la burbuja del chat para ver como le llega al contacto. -->
        <div class="p-4 rounded-xl bg-n-alpha-black2">
          <div
            class="p-3 max-w-sm text-sm whitespace-pre-wrap rounded-lg rounded-tl-none border shadow-sm bg-n-solid-1 border-n-weak text-n-slate-12"
          >
            <strong v-if="form.header" class="block mb-1">
              {{ form.header }}
            </strong>
            {{ previewText }}
            <span v-if="form.footer" class="block mt-2 text-xs text-n-slate-11">
              {{ form.footer }}
            </span>
          </div>
        </div>
      </div>

      <p v-if="formError" class="m-0 text-sm text-n-ruby-11">
        {{ formError }}
      </p>

      <div class="flex gap-2 justify-end px-0 py-2 w-full">
        <NextButton
          faded
          slate
          type="button"
          :label="$t('INBOX_MGMT.WHATSAPP_TEMPLATES.FORM.CANCEL')"
          @click.prevent="onClose"
        />
        <NextButton
          type="submit"
          :label="$t('INBOX_MGMT.WHATSAPP_TEMPLATES.FORM.SUBMIT')"
          :disabled="isIncomplete || isSubmitting"
          :is-loading="isSubmitting"
        />
      </div>
    </form>
  </div>
</template>
